import { datadogRum } from '@datadog/browser-rum'
import debug from '../utils/console'
import { isDebugEnabled } from '../utils/debug/isDebugEnabled'
import {
  AD_VISIBLE_RATIO,
  FIRST_AD_VISIBLE_VITAL,
  createFirstAdVisibleTracker,
  reportAdVisible,
  setFirstAdVisibleTracker,
  stopWatchingAds,
} from '../utils/ad-ops/firstAdVisible'
import type { FirstAdVisibleSample } from '../utils/ad-ops/firstAdVisible'
import { firstAdRequestContext } from '../utils/consent/firstAdRequest'
import {
  activationStart,
  isAnotherPage,
  isPrerendering,
  pageStart,
} from '../utils/rumPage'

const logger = debug.withTag('first-ad-visible')

interface SlotEvent {
  slot: { getAdUnitPath: () => string }
}
interface SlotRenderEndedEvent extends SlotEvent {
  isEmpty: boolean
}
interface SlotVisibilityChangedEvent extends SlotEvent {
  inViewPercentage: number
}

function send(sample: FirstAdVisibleSample) {
  if (isDebugEnabled()) logger.info('First ad visible', sample)
  datadogRum.addDurationVital(FIRST_AD_VISIBLE_VITAL, {
    startTime: sample.startTime,
    duration: sample.duration,
    context: {
      ad_type: sample.adType,
      placement: sample.placement,
      ...firstAdRequestContext(),
    },
  })
}

function report(sample: FirstAdVisibleSample) {
  if (!isPrerendering()) {
    send(sample)
    return
  }
  // An ad that showed before the visitor saw the page was already there when
  // the page was shown.
  document.addEventListener(
    'prerenderingchange',
    () => send({ ...sample, startTime: pageStart(), duration: 0 }),
    { once: true }
  )
}

/**
 * Measures the time to first visible ad for this page load. See
 * utils/ad-ops/firstAdVisible.ts for what counts and where the ad types report.
 *
 * The clock starts when the visitor started seeing the page: the start of the
 * navigation, or the moment a prerendered page was shown. A page that was in a
 * background tab, or that the visitor left for another route before any ad
 * showed, reports nothing, since its timing would be about the visitor and not
 * about the page.
 */
function startTracking() {
  // Everything the measurement leaves running, undone once it is settled.
  const cleanups: Array<() => void> = [stopWatchingAds]
  const tracker = createFirstAdVisibleTracker({
    startTime: pageStart,
    elapsed: () => performance.now() - activationStart(),
    report,
    onSettle: () => cleanups.forEach((cleanup) => cleanup()),
  })
  setFirstAdVisibleTracker(tracker)

  const cancelWhenHidden = () => {
    if (document.visibilityState === 'hidden' && !isPrerendering()) {
      tracker.cancel()
    }
  }
  document.addEventListener('visibilitychange', cancelWhenHidden)
  cleanups.push(() =>
    document.removeEventListener('visibilitychange', cancelWhenHidden)
  )

  // A query change (a category tab) is the same page. Another path is not.
  cleanups.push(
    useRouter().afterEach((to, from) => {
      if (isAnotherPage(to, from)) {
        tracker.cancel()
      }
    })
  )

  // Display ads: a slot counts once it has rendered an ad and half of it is
  // in view, whichever comes later. Not slotOnload: that waits for every
  // asset in the creative, which on production can land seconds after the ad
  // is already showing.
  const rendered = new Set<SlotEvent['slot']>()
  const inView = new Map<SlotEvent['slot'], number>()
  const checkSlot = (slot: SlotEvent['slot']) => {
    if (
      rendered.has(slot) &&
      (inView.get(slot) ?? 0) >= AD_VISIBLE_RATIO * 100
    ) {
      reportAdVisible('display', slot.getAdUnitPath())
    }
  }
  const onRenderEnded = ({ slot, isEmpty }: SlotRenderEndedEvent) => {
    // A refresh that came back empty takes the earlier ad off the slot.
    if (isEmpty) {
      rendered.delete(slot)
      return
    }
    rendered.add(slot)
    checkSlot(slot)
  }
  const onVisibilityChanged = ({
    slot,
    inViewPercentage,
  }: SlotVisibilityChangedEvent) => {
    inView.set(slot, inViewPercentage)
    checkSlot(slot)
  }
  cleanups.push(() => {
    rendered.clear()
    inView.clear()
  })

  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(() => {
    const pubads = window.googletag?.pubads?.()
    if (!pubads || tracker.settled) return
    pubads.addEventListener('slotRenderEnded', onRenderEnded)
    pubads.addEventListener('slotVisibilityChanged', onVisibilityChanged)
    cleanups.push(() => {
      pubads.removeEventListener('slotRenderEnded', onRenderEnded)
      pubads.removeEventListener('slotVisibilityChanged', onVisibilityChanged)
    })
  })

  cancelWhenHidden()
}

export default defineNuxtPlugin({
  name: 'first-ad-visible',
  setup() {
    // Only a measurement, so a failure here must never take the page down
    // with it. Without a tracker, nothing reports and the ads are untouched.
    try {
      startTracking()
    } catch (error) {
      setFirstAdVisibleTracker(undefined)
      logger.warn('Could not start measuring the first visible ad', error)
    }
  },
})
